import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalContent,
  ModalHeader,
  ModalSubmitButton,
  SectionWithBorderField,
  showModal,
} from '@edna/components';
import {FieldError} from '@edna/components/FormField';
import {Stack} from '@edna/components/primitives';
import {useOnChange} from '@edna/hooks';
import {ECurrency, formatNumber} from '@edna/utils';
import {ECurrencySymbol} from '@edna/utils/formatMoney';

import {ModalCenterImage, SecondaryText, Text} from 'src/components/primitives';
import {Banner} from 'src/containers/Banner';
import {PayKeeperLogoIcon} from 'src/icons';
import {BlockImage} from 'src/images';
import {joiResolver} from 'src/utils';

import {
  useCreatePayKeeperAutopaymentOrderMutation,
  useGetPayKeeperAutopaymentInfoQuery,
  useOffPayKeeperAutopaymentMutation,
  useUpdatePayKeeperAutopaymentSumMutation,
} from '../../api';
import {MIN_PAYKEEPER_AMOUNT, VALIDATION_PAYKEEPER_AUTOPAYMENT_AMOUNT} from '../../definitions';
import {sendForm} from '../../sendPayKeeperForm';
import * as S from '../../style';
import {useAutopaymentRecommendedThreshold} from '../useAutopaymentRecommendedThreshold';

type TModalProps = {
  closeModal: TEmptyFunction;
};

const OffAutopaymentModal = React.memo<TModalProps>(({closeModal}) => {
  const {t} = useTranslation();
  const {id: tenantId} = useParams<'id'>();

  const {data: info} = useGetPayKeeperAutopaymentInfoQuery();
  const [offAutopayment] = useOffPayKeeperAutopaymentMutation();

  const handleOff = React.useCallback(() => {
    offAutopayment({userId: info?.userId, tenantId});
    closeModal();
  }, [tenantId, info]);

  return (
    <Modal width="430px">
      <ModalHeader>{t('BalanceReplenishment:payKeeperOffAutopaymentModal.title')}</ModalHeader>
      <ModalContent>
        <SecondaryText>
          {t('BalanceReplenishment:payKeeperOffAutopaymentModal.subtitle')}
        </SecondaryText>
      </ModalContent>
      <ModalCenterImage as={BlockImage} />
      <ModalActions>
        <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
        <ModalSubmitButton onClick={handleOff}>
          {t('BalanceReplenishment:payKeeperOffAutopaymentModal.submitButton')}
        </ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
});

const ChangeAutopaymentCreditsModal = React.memo<TModalProps>(({closeModal}) => {
  const {t} = useTranslation();
  const {id: tenantId} = useParams<'id'>();

  const {data: info} = useGetPayKeeperAutopaymentInfoQuery();
  const [createPayKeeperOrder] = useCreatePayKeeperAutopaymentOrderMutation();

  const handleChangeCredits = React.useCallback(async () => {
    if (!info) {
      return;
    }

    closeModal();

    try {
      const orderInfo = await createPayKeeperOrder({
        recurrentPaymentSum: info.recurrentPaymentSum,
        sum: VALIDATION_PAYKEEPER_AUTOPAYMENT_AMOUNT,
      }).unwrap();

      sendForm(`${orderInfo.paykeeperServerUrl}/create/`, {
        orderid: orderInfo.orderId,
        clientid: orderInfo.clientId,
        client_email: orderInfo.clientEmail,
        service_name: orderInfo.serviceName,
        sum: VALIDATION_PAYKEEPER_AUTOPAYMENT_AMOUNT,
        msgtype: 'createbinding',
      });
    } catch (e) {}
  }, [closeModal, tenantId, info]);

  return (
    <Modal width="530px">
      <ModalHeader>{t('BalanceReplenishment:payKeeperChangeCardCreditsModal.title')}</ModalHeader>
      <ModalContent>
        <S.Wrapper noMargin>
          <SecondaryText size="S">
            {t('BalanceReplenishment:payKeeperChangeCardCreditsModal.subtitle')}
          </SecondaryText>
          <Stack isFullWidth justify="center">
            <PayKeeperLogoIcon />
          </Stack>
          <SecondaryText size="S">
            {t('BalanceReplenishment:payKeeperAutopaymentSettingsModal.validationInfo')}
            {t('BalanceReplenishment:payKeeperAutopaymentSettingsModal.acceptanceInfo')}
          </SecondaryText>
        </S.Wrapper>
      </ModalContent>
      <ModalActions>
        <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
        <ModalSubmitButton onClick={handleChangeCredits}>
          {t('Common:button.continue')}
        </ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
});

const ChangeAutopaymentSumModal = React.memo<TModalProps>(({closeModal}) => {
  const {t} = useTranslation();

  const {data: info} = useGetPayKeeperAutopaymentInfoQuery();
  const [updateSum] = useUpdatePayKeeperAutopaymentSumMutation();
  const recommendedThreshold = useAutopaymentRecommendedThreshold();

  const defaultValues = React.useMemo(() => ({sum: info?.recurrentPaymentSum}), [info]);

  const methods = useForm<{sum: number | null}>({
    mode: 'all',
    defaultValues,
    resolver: joiResolver((joi) =>
      joi.object({
        sum: joi.number().required().min(MIN_PAYKEEPER_AMOUNT),
      }),
    ),
  });

  useOnChange(() => methods.reset(defaultValues), [defaultValues]);

  const sumError = methods.formState.errors.sum;
  const sum = methods.watch('sum');

  const handleSave = React.useCallback(
    (values: {sum: number | null}) => {
      closeModal();
      updateSum(values.sum ?? 0);
    },
    [closeModal],
  );

  return (
    <Modal width="530px">
      <FormProvider {...methods}>
        <ModalHeader>
          {t('BalanceReplenishment:payKeeperAutopaymentSettingsModal.editTitle')}
        </ModalHeader>
        <ModalContent>
          <S.Wrapper>
            {!!recommendedThreshold && (
              <SecondaryText size="S">
                <Trans
                  i18nKey="BalanceReplenishment:payKeeperAutopaymentSettingsModal.licensesInfo"
                  values={{threshold: formatNumber(recommendedThreshold)}}
                >
                  <Text size="S" weight={500} isInline />
                </Trans>
              </SecondaryText>
            )}
            <SectionWithBorderField
              name="sum"
              htmlFor="sum"
              size="1"
              label={t('BalanceReplenishment:payKeeperAutopaymentSettingsModal.amount')}
              hint={t('BalanceReplenishment:payKeeperAutopaymentSettingsModal.minAmount')}
            >
              <Stack size="2" direction="ROW" align="center">
                <S.PayKeeperNumberInputField name="sum" showError={false} noMargin />
                <Text size="S">{ECurrencySymbol[ECurrency.RUB]}</Text>
              </Stack>
              {!!sumError && (
                <FieldError>
                  {t(sumError?.message ?? '', sumError?.ref?.value) as string}
                </FieldError>
              )}
            </SectionWithBorderField>
          </S.Wrapper>
          {!!sum && Number(sum) < recommendedThreshold && (
            <Banner type="WARNING" fontSize="S" shadowSize="XS">
              {t('BalanceReplenishment:payKeeperAutopaymentSettingsModal.lowSumWarning')}
            </Banner>
          )}
          <SecondaryText size="S">
            {t('BalanceReplenishment:payKeeperAutopaymentSettingsModal.acceptanceInfo')}
          </SecondaryText>
        </ModalContent>
        <ModalActions>
          <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
          <ModalSubmitButton
            onClick={methods.handleSubmit(handleSave)}
            disabled={!methods.formState.isValid}
          >
            {t('Common:button.continue')}
          </ModalSubmitButton>
        </ModalActions>
      </FormProvider>
    </Modal>
  );
});

const showOffAutopaymentModal = () =>
  showModal((closeModal) => <OffAutopaymentModal closeModal={closeModal} />);

const showChangeAutopaymentCreditsModal = () =>
  showModal((closeModal) => <ChangeAutopaymentCreditsModal closeModal={closeModal} />);

const showChangeAutopaymentSumModal = () =>
  showModal((closeModal) => <ChangeAutopaymentSumModal closeModal={closeModal} />);

export {showChangeAutopaymentCreditsModal, showChangeAutopaymentSumModal, showOffAutopaymentModal};
